import React from 'react';
import SchedulePageContextType from '@components/SchedulePage/SchedulePageContext/SchedulePageContextType';
import useGetParsedSchedules from '@components/SchedulePage/utilities/useGetParsedSchedules/useGetParsedSchedules';
import { addBusinessDays, format } from 'date-fns';
import useSurgeonsSelectionByNpis
	from '@components/SchedulePage/utilities/useSurgeonsSelectionByNpis/useSurgeonsSelectionByNpis';
import { StorageKey } from '@utilities/authConstants';
import useSessionStorageCache from '@utilities/hooks/useSessionStorageCache/useSessionStorageCache';
import { useFacilitySelection } from '@components/SchedulePage/utilities/useFacilitySelection/useFacilitySelection';
import { Facility } from '@interfaces/Facility';
import { useSurgeons } from '@utilities/hooks/useSurgeons/useSurgeons';
import { useGetBlockReleasesByNpi } from '@data/blockReleases/hooks/useGetBlockReleasesByNpi';
import { BlockReleaseRequest } from '@data/blockReleases/types/BlockRelease.types';
import { BlockReleaseState } from '@data/blockReleases/types/BlockReleaseState';
import { combineFlipReleases } from '@components/BlockRelease/utils/releaseFlipCombiner/combineFlipReleases';
import { sortBlockReleases } from '@components/BlockRelease/utils/sortBlockReleases/sortBlockReleases';

const SchedulePageContext = React.createContext<null | SchedulePageContextType>(null);

export const useSchedulePageContext = () => {
	const ctx = React.useContext(SchedulePageContext);
	if (!ctx) {
		throw new Error('useSchedulePageContext must be used within a child component of SchedulePageProvider');
	}
	return ctx;
};

export const oneBusinessDayFromToday = addBusinessDays(new Date(), 1);

export const SchedulePageProvider: React.FC = ({ children }) => {
	const {
		selectedFacility,
		setSelectedFacility,
		facilitiesSorted,
		getFacilityByHospitalName,
		getFacilityByUnit,
	} = useFacilitySelection();

	const {
		prefSurgeons,
		selectedSurgeonsByNpis,
		setSelectedSurgeonsByNpis,
	} = useSurgeonsSelectionByNpis();

	const {
		apiError,
		schedules,
		setSchedules,
		getApiBlocksAndProcessForUi,
		lastUpdate
	} = useGetParsedSchedules();

	const {
		storedValue: storedDate,
		setStoredValue: setStoredDate
	} = useSessionStorageCache(StorageKey.BLOCK_DATE);

	const { npis } = useSurgeons();
	const { getReleases, releases } = useGetBlockReleasesByNpi();

	const defaultSelectedDate = React.useMemo(() =>
		storedDate ? new Date(storedDate) : oneBusinessDayFromToday
	, [storedDate]);

	const [selectedDate, setSelectedDate] = React.useState(defaultSelectedDate);

	React.useEffect(() => {
		setStoredDate(format(selectedDate, 'yyyy-MM-dd') + ' 00:00');
	}, [selectedDate, setStoredDate]);

	const getSchedules = React.useCallback((date?: Date, npi?: [number], facility?: Facility) => {
		setSchedules([]);
		const inUseDate = date || selectedDate;
		const inUseNpi = npi || selectedSurgeonsByNpis;
		const inUseFacility = facility || selectedFacility;

		if (inUseFacility && inUseNpi?.length) {
			getApiBlocksAndProcessForUi(inUseNpi, inUseFacility, inUseDate);
		}
	}, [getApiBlocksAndProcessForUi, selectedDate, selectedFacility, selectedSurgeonsByNpis, setSchedules]);

	React.useEffect(() => {
		if (npis.length && schedules.length) {
			const requestBody: BlockReleaseRequest = {
				query: 'RELEASE_BY_NPI',
				npis,
				types: [BlockReleaseState.RECOMMENDED_RELEASE],
			};
			getReleases(requestBody, { shouldDisableLoadIndicator: true });
		}
	}, [getReleases, npis, schedules.length]);
	const sortedReleases = React.useMemo(() => sortBlockReleases(releases), [releases]);
	const combinedFlipReleases = React.useMemo(() => combineFlipReleases(sortedReleases), [sortedReleases]);

	return (
		<SchedulePageContext.Provider value={{
			apiError,
			schedules,
			setSchedules,
			getSchedules: getSchedules,
			lastUpdate,
			prefSurgeons,
			selectedSurgeonsByNpis,
			setSelectedSurgeonsByNpis,
			selectedDate,
			setSelectedDate,
			selectedFacility,
			setSelectedFacility,
			facilitiesSorted,
			getFacilityByHospitalName,
			getFacilityByUnit,
			releases: combinedFlipReleases,
		}}>
			{children}
		</SchedulePageContext.Provider>
	);
};
